import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button as BsButton,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useLoaderData, useNavigate, useParams } from "react-router-dom";
import {
  CheckCircleFill,
  CreditCard,
  CurrencyDollar,
  ExclamationTriangleFill,
  Paperclip,
  Receipt,
  XCircleFill,
} from "react-bootstrap-icons";
import { openUsageReceiptDownload, usageBillingReceiptUrl } from "./Data/UsageReceipt";
import { MediumPage } from "./Controls/MediumPage";
import { PageBar } from "./Controls/PageBar";
import { Button } from "./Controls/Button";
import { MoneyInput } from "./Controls/MoneyInput";
import { axiosClient } from "./shared/apiConfig";
import { formatPaymentMethodDisplay } from "./Data/Finance";
import { showMoneyWithCents } from "./Data/Money";
import "./FacilityParkerUsageBillingPage.scss";

const STAGE_LEAVE_MS = 280;
const STAGE_ENTER_MS = 320;
const REVIEW_PAUSE_MS = 350;

// ─── Types ───────────────────────────────────────────────────────────────────

interface UsageChargeHistoryItem {
  orderId: number;
  description: string;
  amount: number;
  totalCharged: number;
  createdWhen: string;
  hasReceipt: boolean;
}

interface UsageBillingPageDto {
  subscriptionId: number;
  parkerId: number;
  facilityId: number;
  parkerName: string;
  parkerEmail: string;
  planName: string;
  subscriptionStatus: string;
  paymentMethodDescription: string | null;
  hasPaymentMethod: boolean;
  recentCharges: UsageChargeHistoryItem[];
}

interface CreateUsageChargeResponse {
  success: boolean;
  error?: string;
  orderId?: number;
}

interface ChargeFormValues {
  description: string;
  amount: number;
}

const maxChargeCents = 1_000_000;
const maxReceiptBytes = 2 * 1024 * 1024;
const allowedReceiptExtensions = [ ".pdf", ".csv", ".xls", ".xlsx" ];
const chargeAccentColor = "#BE185D";
const chargeBadgeBg     = "#FDF2F8";

function validateReceiptFile( file: File ): string | null {
  const ext = file.name.includes( "." )
    ? file.name.slice( file.name.lastIndexOf( "." ) ).toLowerCase()
    : "";
  if ( !allowedReceiptExtensions.includes( ext ) )
    return "Receipt must be a PDF, CSV, or Excel file (.pdf, .csv, .xls, .xlsx).";
  if ( file.size > maxReceiptBytes )
    return "Receipt file must be 2 MB or smaller.";
  return null;
}

function findDuplicateCharge(
  charges: UsageChargeHistoryItem[],
  amountCents: number,
  description: string,
) {
  const desc = description.trim().toLowerCase();
  return charges.find( c =>
    c.description.trim().toLowerCase() === desc ||
    c.amount === amountCents
  );
}

const formatChargeDate = ( iso: string ) => {
  try {
    const d = new Date( iso );
    const date = d.toLocaleDateString( "en-US", {
      year: "numeric", month: "short", day: "numeric",
    } );
    const time = d.toLocaleTimeString( "en-US", {
      hour: "numeric", minute: "2-digit",
    } );
    return `${date} · ${time}`;
  } catch {
    return iso;
  }
};

function UsageChargeRow( { charge }: { charge: UsageChargeHistoryItem } ) {
  return (
    <div className="d-flex align-items-center gap-3 py-3 px-3" style={{ minWidth: 0 }}>
      <span
        className="flex-shrink-0 fw-bold rounded-pill text-nowrap"
        style={{
          backgroundColor: chargeBadgeBg,
          color: chargeAccentColor,
          fontSize: "0.875rem",
          padding: "6px 14px",
        }}
      >
        { showMoneyWithCents( charge.amount ) }
      </span>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="text-truncate" style={{ fontSize: "0.9rem", color: "#212529" }}>
          { charge.description }
        </div>
        <div className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "2px" }}>
          { formatChargeDate( charge.createdWhen ) }
        </div>
      </div>
      { charge.hasReceipt && (
        <BsButton
          variant="outline-secondary"
          size="sm"
          className="flex-shrink-0"
          title="View attached receipt"
          onClick={() => openUsageReceiptDownload( usageBillingReceiptUrl( charge.orderId ) )}
        >
          <Paperclip className="me-1 icon-shift-up" />
          Receipt
        </BsButton>
      ) }
    </div>
  );
}

function RecentChargesList( {
  charges,
}: {
  charges: UsageChargeHistoryItem[];
} ) {
  return (
    <div
      className="bg-white rounded overflow-hidden"
      style={{
        border: "1px solid #dee2e6",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
      }}
    >
      { charges.map( ( charge, index ) => (
        <div
          key={charge.orderId}
          style={ index < charges.length - 1 ? { borderBottom: "1px solid #e9ecef" } : undefined }
        >
          <UsageChargeRow charge={charge} />
        </div>
      ) ) }
    </div>
  );
}

// ─── Charge modal stages ──────────────────────────────────────────────────────

type ModalStage = "entry" | "confirm" | "duplicate" | "success" | "failure";

// ─── Component ───────────────────────────────────────────────────────────────

export function FacilityParkerUsageBillingPage() {
  const dto        = useLoaderData() as UsageBillingPageDto;
  const nav        = useNavigate();
  const { facilityId, parkerId } = useParams<{ facilityId: string; parkerId: string }>();

  const [charges, setCharges]         = useState<UsageChargeHistoryItem[]>( dto.recentCharges );
  const [showModal, setShowModal]     = useState( false );
  const [stage, setStage]             = useState<ModalStage>( "entry" );
  const [stagePhase, setStagePhase]     = useState<"idle" | "leaving" | "entering">( "idle" );
  const [reviewing, setReviewing]       = useState( false );
  const [submitting, setSubmitting]   = useState( false );
  const [duplicateAck, setDuplicateAck] = useState( false );
  const [failureMsg, setFailureMsg]   = useState<string>( "" );
  const [lastCharge, setLastCharge]   = useState<{ amount: number; description: string } | null>( null );
  const [receiptFile, setReceiptFile] = useState<File | null>( null );
  const [receiptFileError, setReceiptFileError] = useState<string>( "" );
  const descriptionRef = useRef<HTMLInputElement | null>( null );
  const receiptFileInputRef = useRef<HTMLInputElement | null>( null );

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ChargeFormValues>( { defaultValues: { description: "", amount: 0 } } );

  const { ref: descriptionRegisterRef, ...descriptionRegister } = register( "description", {
    required: "Description is required.",
    minLength: { value: 10, message: "Description must be at least 10 characters." },
    maxLength: { value: 100, message: "Description cannot exceed 100 characters." },
    validate: ( value ) => !/[\r\n]/.test( value ) || "Description must be on a single line.",
  } );

  const formValues = watch();
  const amountRaw  = watch( "amount" );

  const clearAmount = () => setValue( "amount", 0 );

  useEffect( () => {
    if ( !showModal || stage !== "entry" || stagePhase !== "idle" ) return;
    const id = window.setTimeout( () => descriptionRef.current?.focus(), 100 );
    return () => window.clearTimeout( id );
  }, [showModal, stage, stagePhase] );

  // ── Modal helpers ────────────────────────────────────────────────────────────

  const setStageInstant = useCallback( ( next: ModalStage ) => {
    setStage( next );
    setStagePhase( "idle" );
  }, [] );

  const goToStage = useCallback( ( next: ModalStage ) => {
    if ( stagePhase !== "idle" || next === stage )
      return;

    setStagePhase( "leaving" );
    window.setTimeout( () => {
      setStage( next );
      setStagePhase( "entering" );
      window.setTimeout( () => setStagePhase( "idle" ), STAGE_ENTER_MS );
    }, STAGE_LEAVE_MS );
  }, [stage, stagePhase] );

  const openModal = () => {
    reset();
    setReceiptFile( null );
    setReceiptFileError( "" );
    if ( receiptFileInputRef.current )
      receiptFileInputRef.current.value = "";
    setStageInstant( "entry" );
    setReviewing( false );
    setDuplicateAck( false );
    setFailureMsg( "" );
    setShowModal( true );
  };

  const onReceiptFileChange = ( file: File | null ) => {
    if ( !file ) {
      setReceiptFile( null );
      setReceiptFileError( "" );
      return;
    }
    const err = validateReceiptFile( file );
    setReceiptFileError( err ?? "" );
    setReceiptFile( err ? null : file );
  };

  const clearReceiptFile = () => {
    setReceiptFile( null );
    setReceiptFileError( "" );
    if ( receiptFileInputRef.current )
      receiptFileInputRef.current.value = "";
  };

  const closeModal = () => setShowModal( false );

  const onReviewClick = handleSubmit( ( values ) => {
    if ( receiptFileError || stagePhase !== "idle" || reviewing )
      return;

    const next = findDuplicateCharge( charges, values.amount, values.description )
      ? "duplicate"
      : "confirm";

    setDuplicateAck( false );
    setReviewing( true );
    window.setTimeout( () => {
      setReviewing( false );
      goToStage( next );
    }, REVIEW_PAUSE_MS );
  } );

  const submitCharge = async () => {
    setSubmitting( true );
    try {
      const formData = new FormData();
      formData.append( "Amount", String( formValues.amount ) );
      formData.append( "Description", formValues.description );
      if ( receiptFile )
        formData.append( "Receipt", receiptFile );

      const res = await axiosClient.post<CreateUsageChargeResponse>(
        `/usage-billing/subscriptions/${dto.subscriptionId}/charge`,
        formData,
        {
          // axiosClient defaults to application/json; must omit so browser sets multipart boundary
          transformRequest: [
            ( data, headers ) => {
              if ( data instanceof FormData )
                delete headers["Content-Type"];
              return data;
            },
          ],
        },
      );
      const data = res.data;

      if ( data.success ) {
        setLastCharge( { amount: formValues.amount, description: formValues.description } );
        // prepend to local charges list
        setCharges( prev => [
          {
            orderId:      data.orderId!,
            description:  formValues.description,
            amount:       formValues.amount,
            totalCharged: formValues.amount,
            createdWhen:  new Date().toISOString(),
            hasReceipt:   !!receiptFile,
          },
          ...prev.slice( 0, 5 ),
        ] );
        setStageInstant( "success" );
      } else {
        setFailureMsg( data.error ?? "An unexpected error occurred." );
        setStageInstant( "failure" );
      }
    } catch ( err: any ) {
      const data = err?.response?.data;
      let msg: string;
      if ( typeof data === "string" ) {
        msg = data;
      } else if ( data?.errors ) {
        const parts = Object.values( data.errors as Record<string, string[]> ).flat();
        msg = parts.length ? parts.join( " " ) : data.title ?? "Validation failed.";
      } else {
        msg = data?.error ?? data?.title ?? err?.message ?? "An unexpected error occurred.";
      }
      setFailureMsg( msg );
      setStageInstant( "failure" );
    } finally {
      setSubmitting( false );
    }
  };

  // ── Modal content ────────────────────────────────────────────────────────────

  const modalTitle: Record<ModalStage, string> = {
    entry:     "New Usage Charge",
    confirm:   "Confirm Charge",
    duplicate: "Duplicate Detected",
    success:   "Charge Successful",
    failure:   "Charge Failed",
  };

  const renderEntry = () => (
    <>
      <Modal.Body className="p-4">
        <p className="text-muted small mb-4">
          Enter the details for this usage charge. The amount will be billed immediately to the card on file.
        </p>

        <div className="mb-3">
          <label className="form-label fw-semibold">
            Description <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${ errors.description ? "is-invalid" : "" }`}
            maxLength={100}
            placeholder="e.g. Oversized vehicle fee for June — 10–100 characters"
            {...descriptionRegister}
            ref={( e ) => {
              descriptionRegisterRef( e );
              descriptionRef.current = e;
            }}
          />
          <div className="form-text text-muted text-end">
            { formValues.description?.length ?? 0 }/100
          </div>
          { errors.description && (
            <div className="invalid-feedback">{ errors.description.message }</div>
          ) }
        </div>

        <MoneyInput
          icon={<CurrencyDollar />}
          label="Amount"
          inputClassName="text-end"
          explanation="Usage charge amount in dollars and cents"
          value={amountRaw}
          append={
            <BsButton variant="outline-secondary" className="mx-2" size="sm" type="button" onClick={clearAmount}>
              Clear
            </BsButton>
          }
          {...register( "amount", {
            valueAsNumber: true,
            required: "Amount is required.",
            validate: ( value: number ) => {
              if ( value === 0 || isNaN( value ) )
                return "Amount must be greater than $0.";
              if ( value > maxChargeCents )
                return "Amount cannot exceed $10,000.";
              return true;
            },
          } )}
          error={errors.amount}
        />

        <div className="mb-0">
          <label className="form-label fw-semibold">
            Receipt attachment <span className="text-muted fw-normal">(optional)</span>
          </label>
          <input
            ref={receiptFileInputRef}
            type="file"
            className={`form-control ${ receiptFileError ? "is-invalid" : "" }`}
            accept=".pdf,.csv,.xls,.xlsx"
            onChange={e => onReceiptFileChange( e.target.files?.[0] ?? null )}
          />
          <div className="form-text text-muted">
            PDF, CSV, or Excel — max 2 MB. Attached to the parker&apos;s receipt email.
          </div>
          { receiptFile && !receiptFileError && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <span className="small text-truncate">{ receiptFile.name }</span>
              <BsButton variant="outline-secondary" size="sm" type="button" onClick={clearReceiptFile}>
                Remove
              </BsButton>
            </div>
          ) }
          { receiptFileError && (
            <div className="invalid-feedback d-block">{ receiptFileError }</div>
          ) }
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0 px-4 pb-4">
        <Button onClick={closeModal}>Cancel</Button>
        <Button
          className="btn-main"
          onClick={onReviewClick}
          disabled={!!receiptFileError || reviewing || stagePhase !== "idle"}
          style={{ minWidth: 150 }}
        >
          { reviewing
            ? <><Spinner size="sm" className="me-2" animation="border" />Reviewing…</>
            : "Review Charge →"
          }
        </Button>
      </Modal.Footer>
    </>
  );

  const renderConfirm = () => (
    <>
      <Modal.Body className="p-4">
        <p className="text-muted small mb-4">
          Please review the details below. This charge will be processed immediately and cannot be undone.
        </p>

        <div className="rounded border mb-3 bg-light overflow-hidden">
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="text-muted small mb-1">Account</div>
                <div className="fw-semibold">{ dto.parkerName }</div>
                <div className="text-muted small">{ dto.parkerEmail }</div>
              </div>
              <div className="text-end">
                <div className="text-muted small mb-1">Amount</div>
                <div className="fs-4 fw-bold text-success">
                  { showMoneyWithCents( formValues.amount ) }
                </div>
                <div className="text-muted mt-1 text-nowrap" style={{ fontSize: "0.65rem", lineHeight: 1.2 }}>
                  Does not include applicable fees.
                </div>
              </div>
            </div>
          </div>

          <div className="border-top px-3 py-3">
            <div className="text-muted small mb-1">Description</div>
            <div>{ formValues.description }</div>
          </div>

          <div className="border-top px-3 py-3 d-flex align-items-center gap-2">
            <CreditCard className="text-muted flex-shrink-0" />
            <span className="text-muted small">{ formatPaymentMethodDisplay( dto.paymentMethodDescription, "Card on file" ) }</span>
          </div>

          { receiptFile && (
            <div className="border-top px-3 py-3">
              <div className="text-muted small mb-1">Receipt attachment</div>
              <div className="small text-truncate">{ receiptFile.name }</div>
            </div>
          ) }
        </div>

        <Alert variant="info" className="small py-2 mb-0">
          A receipt will be emailed to <strong>{ dto.parkerEmail }</strong> upon successful charge
          { receiptFile ? ", including your attached document." : "." }
        </Alert>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0 px-4 pb-4 d-flex gap-2">
        <Button onClick={() => goToStage( "entry" )} disabled={stagePhase !== "idle"}>← Back</Button>
        <Button
          className="btn-main"
          onClick={submitCharge}
          disabled={submitting}
          style={{ minWidth: 170 }}
        >
          { submitting
            ? <><Spinner size="sm" className="me-2" />Processing…</>
            : "Confirm & Charge"
          }
        </Button>
      </Modal.Footer>
    </>
  );

  const renderDuplicate = () => {
    const existingCharge = findDuplicateCharge( charges, formValues.amount, formValues.description );

    return (
    <>
      <Modal.Body className="p-4">
        <div className="mb-4">
          <div className="fw-semibold mb-1">This charge may be a duplicate</div>
          <p className="text-muted small mb-0">
            A recent charge with the same amount or description appears in the last 5 charges shown below.
            Please verify this is intentional before proceeding.
          </p>
        </div>

        <div className="rounded border border-warning p-3 bg-warning bg-opacity-10 mb-3">
          <div className="fw-semibold">{ existingCharge?.description ?? formValues.description }</div>
          <div className="text-muted">
            { showMoneyWithCents( existingCharge?.amount ?? formValues.amount ) }
          </div>
          { existingCharge && (
            <div className="text-muted small mt-1">{ formatChargeDate( existingCharge.createdWhen ) }</div>
          ) }
          { receiptFile && (
            <div className="text-muted small mt-2">Attachment: { receiptFile.name }</div>
          ) }
        </div>

        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="dupAck"
            checked={duplicateAck}
            onChange={e => setDuplicateAck( e.target.checked )}
          />
          <label htmlFor="dupAck" className="form-check-label small">
            I understand this may be a duplicate and want to proceed anyway.
          </label>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0 px-4 pb-4 d-flex gap-2">
        <Button onClick={() => goToStage( "entry" )} disabled={stagePhase !== "idle"}>← Edit</Button>
        <Button
          className="btn-main"
          disabled={!duplicateAck || submitting}
          onClick={submitCharge}
          style={{ minWidth: 150 }}
        >
          { submitting
            ? <><Spinner size="sm" className="me-2" />Processing…</>
            : "Charge Anyway"
          }
        </Button>
      </Modal.Footer>
    </>
    );
  };

  const renderSuccess = () => (
    <>
      <Modal.Body className="p-4 text-center">
        <CheckCircleFill size={52} className="text-success mb-3" />
        <h5 className="fw-bold mb-1">Charge Successful</h5>
        <div className="fs-4 fw-semibold text-success mb-2">
          { showMoneyWithCents( formValues.amount ) }
        </div>
        <p className="text-muted small mb-3">
          { lastCharge?.description }
        </p>
        <Alert variant="success" className="small py-2 text-start">
          Receipt emailed to <strong>{ dto.parkerEmail }</strong>
          { receiptFile ? ", with your document attached." : "." }
        </Alert>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0 px-4 pb-4 d-flex gap-2 justify-content-center">
        <Button onClick={closeModal}>Close</Button>
      </Modal.Footer>
    </>
  );

  const renderFailure = () => (
    <>
      <Modal.Body className="p-4">
        <div className="d-flex gap-3 mb-4">
       
          <div>
            <div className="fw-semibold mb-1">Charge Failed</div>
            <p className="text-muted small mb-0">
              The payment could not be processed. Review the error below and try again.
            </p>
          </div>
        </div>

        <Alert variant="danger" className="small">
          { failureMsg }
        </Alert>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0 px-4 pb-4 d-flex gap-2">
        <Button onClick={closeModal}>Close</Button>
        <Button className="btn-main" onClick={() => goToStage( "confirm" )} style={{ minWidth: 140 }}>
          Try Again
        </Button>
      </Modal.Footer>
    </>
  );

  const stageContent: Record<ModalStage, JSX.Element> = {
    entry:     renderEntry(),
    confirm:   renderConfirm(),
    duplicate: renderDuplicate(),
    success:   renderSuccess(),
    failure:   renderFailure(),
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <MediumPage>
      <div className="mb-3">
        <h3 className="fw-bold mb-3">
          <BsButton
            variant="link"
            style={{ margin: "-10px 0 0 -10px" }}
            onClick={() => nav( -1 )}
          >
            ← Back
          </BsButton>
          Usage Billing
        </h3>

        <div
          className="d-flex align-items-center justify-content-between rounded-4 border bg-white p-3"
          style={{ boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)" }}
        >
          <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
            <div
              className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
              style={{ width: 42, height: 42, backgroundColor: chargeBadgeBg, color: "#EC4899" }}
            >
              <CreditCard size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                className="text-uppercase text-muted fw-semibold"
                style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}
              >
                Payment on file
              </div>
              <div className="fw-semibold text-truncate">
                { formatPaymentMethodDisplay( dto.paymentMethodDescription ) }
              </div>
            </div>
          </div>

          { dto.hasPaymentMethod ? (
            <span
              className="badge rounded-pill fw-semibold"
              style={{ backgroundColor: "#D1FAE5", color: "#047857" }}
            >
              • Active
            </span>
          ) : (
            <span
              className="badge rounded-pill fw-semibold"
              style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}
            >
              • Missing
            </span>
          ) }
        </div>
      </div>

    

      {/* ── No-card warning ─────────────────────────────────────────────── */}
      { !dto.hasPaymentMethod && (
        <Alert variant="warning" className="d-flex align-items-center gap-2 mb-4">
          <ExclamationTriangleFill className="flex-shrink-0" />
          <span>
            This subscription has no active credit card on file. The parker must add a payment method before usage charges can be applied.
          </span>
        </Alert>
      ) }

      {/* ── New Charge button ────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <PageBar titleSize={6} titleWeight="semibold" title={<><Receipt className="me-2 icon-shift-up" />Recent Charges</>} />
        <Button
          className="btn-main"
          disabled={!dto.hasPaymentMethod}
          title={ !dto.hasPaymentMethod ? "Add a payment method before charging" : undefined }
          onClick={openModal}
        >
          + New Charge
        </Button>
      </div>

      {/* ── Charges list ─────────────────────────────────────────────────── */}
      { charges.length > 0 ? (
        <>
          <RecentChargesList charges={charges} />
          <div className="text-end mt-2">
            <Link
              to={`/facility/${facilityId}/parker/${parkerId}/order`}
              className="small text-decoration-none fw-semibold"
              style={{ color: chargeAccentColor }}
            >
              View all orders →
            </Link>
          </div>
        </>
      ) : (
        <div
          className="rounded-3 border bg-white text-center px-4 py-5"
          style={{
            border: "1px solid #dee2e6",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div
            className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
            style={{ width: 72, height: 72, backgroundColor: chargeBadgeBg, color: chargeAccentColor }}
          >
            <Receipt size={28} />
          </div>
          <p className="fw-semibold mb-2">No usage charges yet</p>
          <p className="text-secondary small mb-4 mx-auto" style={{ maxWidth: 360 }}>
            Usage charges for this subscription will appear here once you bill the account.
          </p>
          <Button
            className="btn-main px-4"
            disabled={!dto.hasPaymentMethod}
            title={ !dto.hasPaymentMethod ? "Add a payment method before charging" : undefined }
            onClick={openModal}
          >
            + New Charge
          </Button>
        </div>
      ) }

      {/* ── Modal ────────────────────────────────────────────────────────── */}
      <Modal show={showModal} onHide={closeModal} centered size="lg" backdrop="static">
        <Modal.Header closeButton className="border-bottom-0 pb-0 px-4 pt-4">
          <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
            { stage === "success" && <CheckCircleFill className="text-success" /> }
            { stage === "failure" && <XCircleFill className="text-danger" /> }
            { stage === "duplicate" && <ExclamationTriangleFill className="text-warning" /> }
            { modalTitle[stage] }
          </Modal.Title>
        </Modal.Header>
        <div
          className={[
            "usage-charge-modal-stage",
            stagePhase === "leaving" ? "usage-charge-modal-stage--leaving" : "",
            stagePhase === "entering" ? "usage-charge-modal-stage--entering" : "",
          ].filter( Boolean ).join( " " )}
        >
          { stageContent[stage] }
        </div>
      </Modal>
    </MediumPage>
  );
}
